import type { BoardSide, BoardSource, BoardView, OriginPlayer, PositionView } from '@/types/myBoards';

/**
 * La logica PURA di «My Boards» (T0): quale riga scrivere e quale filtro applicare, senza React e senza rete — la
 * esercita `tests/my-boards-riga.test.ts`. Nessuna aritmetica della posizione qui (pip, blot, punti, prime): arriva da
 * `positionStats` di `qgammon-rules` (Q3, ADR-0191 §5) e NON si ricopia.
 */

export type FiltroBoard = 'all' | BoardSource;

export const FILTRI_BOARD: readonly FiltroBoard[] = ['all', 'player', 'spectator', 'replay'];

/**
 * Il filtro come lo vuole il server (`GET /boards?source=`): «Tutti» non manda niente. Il filtro e' LATO SERVER perche' la lista
 * e' a cursore: filtrare nel client taglierebbe una pagina da 20 e lascerebbe i buchi.
 */
export function sorgenteDelFiltro(filtro: FiltroBoard): BoardSource | undefined {
  return filtro === 'all' ? undefined : filtro;
}

/**
 * Il nome di un giocatore come la lista lo scrive: «Tu» per chi guarda, un nome, o «giocatore non piu' presente» (ADR-0191 §3:
 * un account cancellato ha `userId` e `displayName` a `null`). Un nome `null` non e' mai scritto come «null».
 */
export type NomeGiocatore = { kind: 'me' } | { kind: 'name'; name: string } | { kind: 'gone' };

export function nomeGiocatore(p: OriginPlayer, mioId: string | null): NomeGiocatore {
  if (p.userId !== null && mioId !== null && p.userId === mioId) return { kind: 'me' };
  if (p.displayName === null || (p.userId === null && !p.isBot)) return { kind: 'gone' };
  return { kind: 'name', name: p.displayName };
}

/**
 * Il contesto della riga (T4): «Finita» se la partita e' finita, altrimenti il TORNEO, la pratica contro un bot, o come si
 * giocava (classificata / fra amici). Il nome del torneo non c'e': QGammon non lo conosce (ADR-0191 §4).
 * `plain` = «non so»: `rated` e' `null` per una partita che precede la colonna, e NON e' «amichevole» — la riga omette
 * «Classificata/Amichevole» e dice solo quando.
 */
export type ContestoBoard = 'finished' | 'tournament' | 'practice' | 'ranked' | 'friendly' | 'plain';

export function contestoDi(board: BoardView): ContestoBoard {
  const { origin } = board;
  if (origin.status === 'finished') return 'finished';
  if (origin.tournamentMatchId !== null) return 'tournament';
  if (origin.players.W.isBot || origin.players.B.isBot) return 'practice';
  if (origin.rated === null) return 'plain';
  return origin.rated ? 'ranked' : 'friendly';
}

/** Chi vince nell'esito, o `null` se la partita e' viva o non ha un vincitore. */
export function latoVincente(board: BoardView): BoardSide | null {
  return board.origin.result?.winner ?? null;
}

/**
 * Gli orologi alla cattura come «m:ss» (T5). ⚠️ `clocksAtCapture` e' in MILLISECONDI (`ClocksAtCaptureSchema`, boards.ts), il
 * `timeControl` in secondi: il T0 li teneva in secondi e dava «1500:00» a un orologio di 90 secondi.
 */
export function minutiSecondiDaMs(ms: number): string {
  const totale = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(totale / 60)}:${String(totale % 60).padStart(2, '0')}`;
}

/** Quando e' stato salvato, rispetto a `adesso`: oggi, ieri o una data (nel fuso locale del telefono). */
export type Momento = { kind: 'today' | 'yesterday' } | { kind: 'date' };

export function momentoDi(createdAt: string, adesso: Date): Momento {
  const quando = new Date(createdAt);
  const giorno = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((giorno(adesso) - giorno(quando)) / 86_400_000);
  if (diff === 0) return { kind: 'today' };
  if (diff === 1) return { kind: 'yesterday' };
  return { kind: 'date' };
}

/** Il lato che si trova in vantaggio di pip (il minore), o `null` in parita'. Solo un confronto, nessun calcolo di pip. */
export function latoInVantaggio(pip: Record<BoardSide, number>): BoardSide | null {
  if (pip.W === pip.B) return null;
  return pip.W < pip.B ? 'W' : 'B';
}

/**
 * La forma del cubo sulla scheda: «ignoto» se la partita e' nata prima del cubo per ply (ADR-0191 §7), «centrato» se nessuno
 * lo possiede, altrimenti valore e proprietario. Niente «Crawford»: nella partita da un board il cubo e' spento
 * (`cubeless`, L-2749 (a)), e un'etichetta Crawford qui direbbe una cosa che non vale.
 */
export type ForaCubo = { kind: 'unknown' } | { kind: 'centered'; value: number } | { kind: 'owned'; value: number; owner: BoardSide };

export function formaCubo(position: Pick<PositionView, 'cube'>): ForaCubo {
  const { value, owner, known } = position.cube;
  // `known: false` ⇒ `value` e `owner` sono `null` (ADR-0191 §7); il `value === null` con `known: true` non esiste nel
  // contratto, e se arrivasse e' «ignoto», non un 1.
  if (!known || value === null) return { kind: 'unknown' };
  if (owner === null) return { kind: 'centered', value };
  return { kind: 'owned', value, owner };
}

/**
 * «Sfida un amico da qui» si offre solo se la posizione e' giocabile (ADR-0191 §8): una di fine gioco si guarda, non si gioca.
 * La regola e' `scenario !== null` (lo scenario lo calcola il SERVER, `null` = non giocabile) e `playable` e' il controllo: se
 * i due non coincidono, NON si offre (un bottone che porta a una sfida impossibile e' peggio di una nota).
 */
export function sfidabile(position: Pick<PositionView, 'playable'>, scenario: unknown | null): boolean {
  return position.playable && scenario !== null;
}

/** Il lato che tira per primo nella partita da un board: e' chi e' di turno (ADR-0191 §6, «il lato di turno tira per primo»). */
export function latoCheTira(position: Pick<PositionView, 'turn'>): BoardSide {
  return position.turn;
}
